/**
 * ARCOIRIS CUSTOM - Protection des précommandes dans le panier
 *
 * Un article de précommande transporte deux propriétés privées :
 *
 * _arcoiris_preorder_start
 * _arcoiris_preorder_end
 *
 * Ces propriétés ne sont pas affichées au client.
 *
 * Si au moins un article du panier est :
 * - en preview
 * - ou après sa date de fermeture
 *
 * alors :
 * - le bouton Commander est désactivé
 * - les paiements accélérés sont masqués
 * - un message explique pourquoi
 *
 * Le verrou est recalculé automatiquement lorsque
 * le panier change ou lorsqu'une date est atteinte.
 */

const MAX_TIMEOUT = 60 * 60 * 1000;

let currentCart = null;
let timerId = null;


/**
 * Convertit une date provenant d'une propriété panier.
 */
const parseDate = (value) => {
  if (!value) return null;

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};


/**
 * Retourne l'état de précommande d'un article.
 *
 * null    = article normal
 * preview = pas encore disponible
 * open    = précommande ouverte
 * closed  = précommande terminée
 */
const getItemState = (item) => {
  const properties = item.properties || {};

  const startDate = parseDate(
    properties._arcoiris_preorder_start
  );

  /**
   * Sans date de début privée :
   * ce n'est pas un article géré par notre workflow.
   */
  if (!startDate) return null;

  const endDate = parseDate(
    properties._arcoiris_preorder_end
  );

  const now = Date.now();

  if (now < startDate.getTime()) {
    return 'preview';
  }

  if (endDate && now >= endDate.getTime()) {
    return 'closed';
  }

  return 'open';
};


/**
 * Retourne les articles qui rendent le panier non commandable.
 */
const getLockedItems = (cart) => {
  if (!cart || !Array.isArray(cart.items)) return [];

  return cart.items.filter((item) => {
    const state = getItemState(item);

    return state === 'preview' || state === 'closed';
  });
};


/**
 * Applique ou retire le verrou sur tous les paniers Venue présents.
 *
 * Cela couvre notamment :
 * - drawer panier
 * - page /cart
 */
/**
 * Applique ou retire le verrou sur tous les paniers Venue présents.
 *
 * Cela couvre notamment :
 * - drawer panier
 * - page /cart
 */
const updateCartInterface = () => {
  const lockedItems = getLockedItems(currentCart);
  const locked = lockedItems.length > 0;

  document
    .querySelectorAll('interactive-cart')
    .forEach((cartElement) => {
      const checkoutButton =
        cartElement.querySelector('.cart__checkout');

      const dynamicButton =
        cartElement.querySelector(
          '.additional-checkout-buttons'
        )?.closest('dynamic-button');

      const message =
        cartElement.querySelector(
          '[data-arcoiris-cart-preorder-message]'
        );

      /**
       * Panier verrouillé
       */
      if (locked) {
        if (checkoutButton) {
          checkoutButton.disabled = true;
          checkoutButton.setAttribute(
            'aria-disabled',
            'true'
          );
          checkoutButton.dataset.arcoirisPreorderLocked =
            'true';
        }

        if (
          dynamicButton &&
          !dynamicButton.hasAttribute(
            'data-arcoiris-preorder-hidden'
          )
        ) {
          dynamicButton.dataset.arcoirisPreorderHidden =
            'true';
          dynamicButton.hidden = true;
        }

        /**
         * Message d'erreur avec le nom
         * du ou des articles concernés.
         */
        if (message) {
          const productTitles = lockedItems
            .map(
              (item) =>
                item.product_title || item.title
            )
            .filter(Boolean);

          const articleLabel =
            productTitles.length === 1
              ? `Article concerné : ${productTitles[0]}`
              : `Articles concernés : ${productTitles.join(', ')}`;

          const actionLabel =
            productTitles.length === 1
              ? 'Retirez cet article du panier pour poursuivre votre commande.'
              : 'Retirez ces articles du panier pour poursuivre votre commande.';

          message.replaceChildren();

          [
            'Précommande indisponible',
            articleLabel,
            actionLabel
          ].forEach((text, index) => {
            const line =
              document.createElement('span');

            line.textContent = text;
            line.style.display = 'block';

            if (index === 0) {
              line.style.fontWeight = '700';
            }

            message.appendChild(line);
          });

          message.hidden = false;
        }

        /**
         * Important :
         * on s'arrête ici pour ne pas exécuter
         * la logique de déverrouillage juste après.
         */
        return;
      }

      /**
       * Panier à nouveau commandable.
       *
       * On ne retire que les modifications
       * que notre propre script avait ajoutées.
       */
      if (
        checkoutButton &&
        checkoutButton.dataset.arcoirisPreorderLocked ===
          'true'
      ) {
        checkoutButton.disabled = false;
        checkoutButton.removeAttribute('aria-disabled');
        delete checkoutButton.dataset.arcoirisPreorderLocked;
      }

      if (
        dynamicButton &&
        dynamicButton.dataset.arcoirisPreorderHidden ===
          'true'
      ) {
        dynamicButton.hidden = false;
        delete dynamicButton.dataset.arcoirisPreorderHidden;
      }

      if (message) {
        message.hidden = true;
        message.replaceChildren();
      }
    });
};



/**
 * Programme la prochaine frontière temporelle du panier.
 *
 * Exemple :
 * précommande ouverte jusqu'à 16:30
 * → le panier se verrouille à 16:30 sans refresh.
 */
const scheduleNextChange = () => {
  if (timerId) {
    clearTimeout(timerId);
    timerId = null;
  }

  if (!currentCart?.items) return;

  const now = Date.now();

  const futureDates = [];

  currentCart.items.forEach((item) => {
    const properties = item.properties || {};

    const startDate = parseDate(
      properties._arcoiris_preorder_start
    );

    const endDate = parseDate(
      properties._arcoiris_preorder_end
    );

    if (
      startDate &&
      startDate.getTime() > now
    ) {
      futureDates.push(startDate.getTime());
    }

    if (
      endDate &&
      endDate.getTime() > now
    ) {
      futureDates.push(endDate.getTime());
    }
  });

  if (futureDates.length === 0) return;

  const nextDate = Math.min(...futureDates);
  const remaining = nextDate - Date.now();

  timerId = window.setTimeout(() => {
    updateCartInterface();
    scheduleNextChange();
  }, Math.min(Math.max(remaining, 0), MAX_TIMEOUT));
};


/**
 * Met à jour notre copie du panier puis l'interface.
 */
const applyCart = (cart) => {
  currentCart = cart;

  updateCartInterface();
  scheduleNextChange();

  /**
   * Venue peut terminer certaines mises à jour DOM
   * juste après l'événement panier.
   */
  window.requestAnimationFrame(() => {
    updateCartInterface();
  });
};


/**
 * Venue émet cet événement après chaque modification du panier.
 */
document.addEventListener('on:cart:loaded', (event) => {
  if (event.detail?.cart) {
    applyCart(event.detail.cart);
  }
});


/**
 * Dernière barrière avant soumission du checkout.
 */
document.addEventListener(
  'submit',
  (event) => {
    const submitter = event.submitter;

    if (
      !submitter ||
      submitter.name !== 'checkout'
    ) {
      return;
    }

    if (getLockedItems(currentCart).length > 0) {
      event.preventDefault();
      event.stopImmediatePropagation();

      updateCartInterface();
    }
  },
  true
);


/**
 * État initial.
 *
 * Le panier Venue étant accessible via /cart.js,
 * on récupère son contenu au chargement de la page.
 */
const loadInitialCart = async () => {
  try {
    const response = await fetch(
      `${window.Shopify?.routes?.root || '/'}cart.js`
    );

    if (!response.ok) return;

    const cart = await response.json();

    applyCart(cart);
  } catch (error) {
    console.error(
      'ArcoIris preorder cart protection:',
      error
    );
  }
};

loadInitialCart();